import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { AppLink, hrefOrClick } from '../linkifier.js';

const outcomeTooltips: Record<WireTypes.Outcome, string> = {
  regressed: 'Regressed: previously-passing tests that started failing, or new tests that failed',
  unexpected: 'Failed: tests that failed and were already failing recently',
  flaked: 'Flaked: tests that passed after retry',
  expected: 'Passed: tests that passed on the first try',
  skipped: 'Skipped: tests that were not run',
};

@customElement('outcomes-bar')
export class OutcomesBar extends LitElement {
  @property({ attribute: false }) counts?: WireTypes.OutcomesCount;
  @property({ attribute: false }) linkifier?: (outcome: WireTypes.Outcome) => AppLink;

  render() {
    const counts = this.counts;
    if (!counts)
      return nothing;
    const outcomes: WireTypes.Outcome[] = (['regressed', 'unexpected', 'flaked', 'expected', 'skipped'] as const).filter(outcome => !!counts[outcome]);
    return html`
      <v-box>
        <h-box style="gap: var(--sl-spacing-x-small); font-size: var(--sl-font-size-small); justify-content: center;">
          ${outcomes.map((outcome, idx) => {
            const link = this.linkifier?.(outcome);
            const inner = html`
              <fk-outcome outcome=${outcome}></fk-outcome>
              <span>${counts[outcome]}</span>
            `;
            const content = link
              ? html`<a class=entry ${hrefOrClick(link)}>${inner}</a>`
              : html`<span class=entry>${inner}</span>`;
            return html`
              ${idx === 0 ? nothing : html`<span style="color: var(--sl-color-neutral-300)">•</span>`}
              <sl-tooltip hoist content=${outcomeTooltips[outcome]}>
                ${content}
              </sl-tooltip>
            `;
          })}
        </h-box>
        <h-box class=barchart>
          ${outcomes.map(outcome => html`
            <div class=${classMap({
              [outcome]: true,
              segment: true,
            })} style=${styleMap({
              'flex-grow': (counts[outcome]),
            })}></div>
          `)}
        </h-box>
      </v-box>
    `;
  }

  static styles = css`
    :host {
    }

    .entry {
      display: inline-flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
      color: inherit;
      text-decoration: none;
    }

    a.entry:hover {
      text-decoration: underline;
    }

    .barchart {
      gap: 1px;
      border-radius: 4px;
      overflow: hidden;
      box-sizing: border-box;
    }

    .segment {
      background-color: color-mix(in oklch, white 50%, var(--color));
      min-width: 14px;
      height: 7px;
    }

    .regressed {
      --color: var(--fk-color-outcome-regressed);
    }

    .expected {
      --color: var(--fk-color-outcome-expected);
    }

    .unexpected {
      --color: var(--fk-color-outcome-unexpected);
    }

    .skipped {
      --color: var(--fk-color-outcome-skipped);
    }

    .flaked {
      --color: var(--fk-color-outcome-flaked);
    }
  `;
}
